/**
 * Browser audio utilities for Gemini Live relay.
 * Capture: mic Float32 -> PCM16 mono 16 kHz via AudioWorklet.
 * Playback: PCM16 mono 24 kHz chunks scheduled gap-free.
 */

const CAPTURE_SAMPLE_RATE = 16000;
const PLAYBACK_SAMPLE_RATE = 24000;

const CAPTURE_WORKLET_SOURCE = `
class CaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const input = inputs[0];
    if (!input || !input[0] || input[0].length === 0) return true;
    const channel = input[0];
  const copy = new Float32Array(channel.length);
    copy.set(channel);
    this.port.postMessage(copy);
    return true;
  }
}
registerProcessor('capture-processor', CaptureProcessor);
`;

let workletUrl = null;

function getWorkletUrl() {
  if (!workletUrl) {
    const blob = new Blob([CAPTURE_WORKLET_SOURCE], { type: 'application/javascript' });
    workletUrl = URL.createObjectURL(blob);
  }
  return workletUrl;
}

function downsampleTo16k(float32, inputRate) {
  if (inputRate === CAPTURE_SAMPLE_RATE) {
    return float32ToPcm16(float32);
  }
  const ratio = inputRate / CAPTURE_SAMPLE_RATE;
  const outLen = Math.floor(float32.length / ratio);
  const pcm = new Int16Array(outLen);
  for (let i = 0; i < outLen; i += 1) {
    const start = Math.floor(i * ratio);
    const end = Math.floor((i + 1) * ratio);
    let sum = 0;
    let count = 0;
    for (let j = start; j < end && j < float32.length; j += 1) {
      sum += float32[j];
      count += 1;
    }
    const sample = count > 0 ? sum / count : 0;
    const clamped = Math.max(-1, Math.min(1, sample));
    pcm[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return pcm;
}

function float32ToPcm16(float32) {
  const pcm = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i += 1) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return pcm;
}

export function createCaptureContext() {
  const audioContext = new AudioContext({ sampleRate: CAPTURE_SAMPLE_RATE });
  return audioContext;
}

export async function startMicCapture(audioContext, onPcmChunk) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
    video: false,
  });

  await audioContext.audioWorklet.addModule(getWorkletUrl());

  const source = audioContext.createMediaStreamSource(stream);
  const worklet = new AudioWorkletNode(audioContext, 'capture-processor');
  const inputRate = audioContext.sampleRate;

  worklet.port.onmessage = (event) => {
    const float32 = event.data;
    if (!(float32 instanceof Float32Array)) return;
    const pcm = downsampleTo16k(float32, inputRate);
    onPcmChunk(pcm.buffer);
  };

  source.connect(worklet);
  // Worklet must be connected to destination to process (gain 0 to avoid feedback)
  const mute = audioContext.createGain();
  mute.gain.value = 0;
  worklet.connect(mute);
  mute.connect(audioContext.destination);

  return {
    stream,
    stop() {
      stream.getTracks().forEach((t) => t.stop());
      source.disconnect();
      worklet.disconnect();
      mute.disconnect();
    },
  };
}

export function createPlaybackContext() {
  return new AudioContext({ sampleRate: PLAYBACK_SAMPLE_RATE });
}

export function createPlaybackScheduler(audioContext) {
  let nextStartTime = audioContext.currentTime;
  const sources = [];

  function enqueuePcm(arrayBuffer) {
    const int16 = new Int16Array(arrayBuffer);
    if (int16.length === 0) return;

    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i += 1) {
      float32[i] = int16[i] / (int16[i] < 0 ? 0x8000 : 0x7fff);
    }

    const buffer = audioContext.createBuffer(1, float32.length, PLAYBACK_SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);

    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);

    const now = audioContext.currentTime;
    if (nextStartTime < now) {
      nextStartTime = now + 0.05;
    }

    source.start(nextStartTime);
    nextStartTime += buffer.duration;
    sources.push(source);

    source.onended = () => {
      const idx = sources.indexOf(source);
      if (idx >= 0) sources.splice(idx, 1);
    };
  }

  function clearPlayback() {
    sources.forEach((s) => {
      try {
        s.stop();
      } catch {
        // already stopped
      }
    });
    sources.length = 0;
    nextStartTime = audioContext.currentTime;
  }

  return { enqueuePcm, clearPlayback };
}

export { CAPTURE_SAMPLE_RATE, PLAYBACK_SAMPLE_RATE };
